import { Container, Row, Col, Card, Button, Alert, Spinner, Form } from 'react-bootstrap';
import { RefreshCw, AlertCircle, CheckCircle, Upload, Package, Filter, Cloud, Share2 } from 'lucide-react';
import { useSiteCache } from '../../../apps/hooks/site-cache/useSiteCache';
import { useState } from 'react';

const SiteCacheManagementPage = () => {
  const [skuInput, setSkuInput] = useState('');

  const {
    invalidateCache,
    isInvalidatingCache,
    isInvalidating,
    isLoading,
    error,
    generateGoogleProductFeed,
    isGeneratingGoogleProductFeed,
    hasRunningGoogleProductFeedJob,
    googleProductFeedError,
    isGmcSyncDisabledForEnvironment,
    lastGoogleProductFeedJob,
    // New invalidation methods
    invalidateProductsByStatus,
    isInvalidatingProductsByStatus,
    invalidateProductsByStatusError,
    clearProductCacheBySKUs,
    isClearingProductCacheBySKUs,
    clearProductCacheBySKUsError,
    purgeCloudflareCache,
    isPurgingCloudflareCache,
    purgeCloudflareCacheError,
    generateMetaCatalogFeed,
    isGeneratingMetaCatalogFeed,
    hasRunningMetaCatalogFeedJob,
    metaCatalogFeedError,
    lastMetaCatalogFeedJob,
  } = useSiteCache();

  const handleInvalidateCache = () => {
    invalidateCache();
  };

  const handleGenerateGoogleProductFeed = () => {
    generateGoogleProductFeed();
  };

  const handleGenerateMetaCatalogFeed = () => {
    generateMetaCatalogFeed();
  };

  const handleInvalidateDraftProducts = () => {
    invalidateProductsByStatus('draft');
  };

  const handleClearProductCacheBySKUs = () => {
    if (skuInput.trim()) {
      clearProductCacheBySKUs(skuInput.trim());
    }
  };

  const handlePurgeCloudflareCache = () => {
    purgeCloudflareCache();
  };

  const isDisabled = isInvalidating || isInvalidatingCache;
  const isCloudflarePurgeDisabled = isPurgingCloudflareCache || isInvalidating;
  const isGmcDisabled =
    isGeneratingGoogleProductFeed || hasRunningGoogleProductFeedJob || isGmcSyncDisabledForEnvironment;
  const isMetaFeedDisabled =
    isGeneratingMetaCatalogFeed || hasRunningMetaCatalogFeedJob || isGmcSyncDisabledForEnvironment;

  // Check if we have a completed job (not running)
  const hasCompletedJob =
    lastGoogleProductFeedJob &&
    !['SUBMITTED', 'PENDING', 'RUNNABLE', 'STARTING', 'RUNNING'].includes(lastGoogleProductFeedJob.status);
  const lastJobSucceeded = lastGoogleProductFeedJob?.status === 'SUCCEEDED';
  const lastJobFailed = lastGoogleProductFeedJob?.status === 'FAILED';

  const hasCompletedMetaJob =
    lastMetaCatalogFeedJob &&
    !['SUBMITTED', 'PENDING', 'RUNNABLE', 'STARTING', 'RUNNING'].includes(lastMetaCatalogFeedJob.status);
  const lastMetaJobSucceeded = lastMetaCatalogFeedJob?.status === 'SUCCEEDED';
  const lastMetaJobFailed = lastMetaCatalogFeedJob?.status === 'FAILED';

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <h1 className="mb-4">Site Cache Management</h1>
        </Col>
      </Row>

      {/* Invalidate Product by SKUs */}
      <Row className="mb-4">
        <Col md={8} lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <Package className="me-2" size={20} />
                Clear Product Cache by SKUs
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Clear product cache by SKUs (works for any publication status). Next read will fetch fresh data.
              </p>

              {clearProductCacheBySKUsError && (
                <Alert variant="danger" className="mb-3">
                  <AlertCircle className="me-2" size={16} />
                  Error:{' '}
                  {clearProductCacheBySKUsError instanceof Error
                    ? clearProductCacheBySKUsError.message
                    : 'An error occurred'}
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>SKUs (comma-separated)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 100014654, 100014655"
                  value={skuInput}
                  onChange={(e) => setSkuInput(e.target.value)}
                  disabled={isClearingProductCacheBySKUs}
                />
              </Form.Group>

              <Button
                variant="primary"
                onClick={handleClearProductCacheBySKUs}
                disabled={isClearingProductCacheBySKUs || !skuInput.trim()}
                className="d-flex align-items-center"
              >
                {isClearingProductCacheBySKUs ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Package className="me-2" size={16} />
                    Clear Cache by SKUs
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Invalidate Draft Products */}
      <Row className="mb-4">
        <Col md={8} lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <Filter className="me-2" size={20} />
                Invalidate Draft Products Only
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Invalidate product cache for draft products only. This is a fast operation that affects all draft
                products.
              </p>

              {invalidateProductsByStatusError && (
                <Alert variant="danger" className="mb-3">
                  <AlertCircle className="me-2" size={16} />
                  Error:{' '}
                  {invalidateProductsByStatusError instanceof Error
                    ? invalidateProductsByStatusError.message
                    : 'An error occurred'}
                </Alert>
              )}

              <Button
                variant="primary"
                onClick={handleInvalidateDraftProducts}
                disabled={isInvalidatingProductsByStatus}
                className="d-flex align-items-center"
              >
                {isInvalidatingProductsByStatus ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Invalidating...
                  </>
                ) : (
                  <>
                    <Filter className="me-2" size={16} />
                    Invalidate Draft Products
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={8} lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <RefreshCw className="me-2" size={20} />
                Invalidate Site Cache
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Invalidate the site cache to ensure all users see the latest content. This process may take a few
                minutes to complete.
              </p>

              {error && (
                <Alert variant="danger" className="mb-3">
                  <AlertCircle className="me-2" size={16} />
                  Error: {error instanceof Error ? error.message : 'An error occurred'}
                </Alert>
              )}

              {isInvalidating && (
                <Alert variant="warning" className="mb-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cache invalidation is currently in progress...
                </Alert>
              )}

              {!isInvalidating && !isLoading && !error && (
                <Alert variant="success" className="mb-3">
                  <CheckCircle className="me-2" size={16} />
                  Cache is ready for invalidation
                </Alert>
              )}

              <Button
                variant="primary"
                onClick={handleInvalidateCache}
                disabled={isDisabled}
                className="d-flex align-items-center"
              >
                {isInvalidatingCache ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Invalidating...
                  </>
                ) : isInvalidating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    In Progress...
                  </>
                ) : (
                  <>
                    <RefreshCw className="me-2" size={16} />
                    Invalidate Cache
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={8} lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <Cloud className="me-2" size={20} />
                Purge Cloudflare Cache
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Purge Cloudflare Worker cache only. This is a fast operation that clears Cloudflare's edge cache.
              </p>

              {purgeCloudflareCacheError && (
                <Alert variant="danger" className="mb-3">
                  <AlertCircle className="me-2" size={16} />
                  Error:{' '}
                  {purgeCloudflareCacheError instanceof Error
                    ? purgeCloudflareCacheError.message
                    : 'An error occurred'}
                </Alert>
              )}

              {(isInvalidating || isPurgingCloudflareCache) && (
                <Alert variant="warning" className="mb-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cache invalidation is currently in progress...
                </Alert>
              )}

              {!isInvalidating && !isPurgingCloudflareCache && !purgeCloudflareCacheError && (
                <Alert variant="success" className="mb-3">
                  <CheckCircle className="me-2" size={16} />
                  Ready to purge Cloudflare cache
                </Alert>
              )}

              <Button
                variant="primary"
                onClick={handlePurgeCloudflareCache}
                disabled={isCloudflarePurgeDisabled}
                className="d-flex align-items-center"
              >
                {isPurgingCloudflareCache ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Purging...
                  </>
                ) : isInvalidating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    In Progress...
                  </>
                ) : (
                  <>
                    <Cloud className="me-2" size={16} />
                    Purge Cloudflare Cache
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={8} lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <Upload className="me-2" size={20} />
                GMC Product Sync
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Generate and sync product feed to Google Merchant Center. This process may take a few minutes to
                complete.
              </p>

              {isGmcSyncDisabledForEnvironment && (
                <Alert variant="warning" className="mb-3">
                  <AlertCircle className="me-2" size={16} />
                  GMC sync is not available in development and staging environments.
                </Alert>
              )}

              {!isGmcSyncDisabledForEnvironment && googleProductFeedError && (
                <Alert variant="danger" className="mb-3">
                  <AlertCircle className="me-2" size={16} />
                  Error:{' '}
                  {googleProductFeedError instanceof Error ? googleProductFeedError.message : 'An error occurred'}
                </Alert>
              )}

              {!isGmcSyncDisabledForEnvironment && hasRunningGoogleProductFeedJob && (
                <Alert variant="warning" className="mb-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Product feed sync with GMC is currently in progress...
                </Alert>
              )}

              {!isGmcSyncDisabledForEnvironment && hasCompletedJob && lastJobSucceeded && (
                <Alert variant="success" className="mb-3">
                  <CheckCircle className="me-2" size={16} />
                  Last GMC product sync completed successfully at{' '}
                  {new Date(lastGoogleProductFeedJob.stoppedAt || lastGoogleProductFeedJob.createdAt).toLocaleString()}
                </Alert>
              )}

              {!isGmcSyncDisabledForEnvironment && hasCompletedJob && lastJobFailed && (
                <Alert variant="danger" className="mb-3">
                  <AlertCircle className="me-2" size={16} />
                  Last GMC product sync failed at{' '}
                  {new Date(lastGoogleProductFeedJob.stoppedAt || lastGoogleProductFeedJob.createdAt).toLocaleString()}
                </Alert>
              )}

              {!isGmcSyncDisabledForEnvironment &&
                !hasRunningGoogleProductFeedJob &&
                !googleProductFeedError &&
                !hasCompletedJob && (
                  <Alert variant="success" className="mb-3">
                    <CheckCircle className="me-2" size={16} />
                    Ready to sync product feed with GMC
                  </Alert>
                )}

              <Button
                variant="primary"
                onClick={handleGenerateGoogleProductFeed}
                disabled={isGmcDisabled}
                className="d-flex align-items-center"
              >
                {isGmcSyncDisabledForEnvironment ? (
                  <>
                    <Upload className="me-2" size={16} />
                    Sync Product Feed with GMC
                  </>
                ) : isGeneratingGoogleProductFeed ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Syncing...
                  </>
                ) : hasRunningGoogleProductFeedJob ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Job in progress...
                  </>
                ) : (
                  <>
                    <Upload className="me-2" size={16} />
                    Sync Product Feed with GMC
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={8} lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <Share2 className="me-2" size={20} />
                Meta Catalog Feed Sync
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Generate and sync product catalog feed to Meta (Facebook/Instagram). This process may take a few minutes
                to complete.
              </p>

              {isGmcSyncDisabledForEnvironment && (
                <Alert variant="warning" className="mb-3">
                  <AlertCircle className="me-2" size={16} />
                  Meta catalog feed sync is not available in development and staging environments.
                </Alert>
              )}

              {!isGmcSyncDisabledForEnvironment && metaCatalogFeedError && (
                <Alert variant="danger" className="mb-3">
                  <AlertCircle className="me-2" size={16} />
                  Error:{' '}
                  {metaCatalogFeedError instanceof Error ? metaCatalogFeedError.message : 'An error occurred'}
                </Alert>
              )}

              {!isGmcSyncDisabledForEnvironment && hasRunningMetaCatalogFeedJob && (
                <Alert variant="warning" className="mb-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Meta catalog feed sync is currently in progress...
                </Alert>
              )}

              {!isGmcSyncDisabledForEnvironment && hasCompletedMetaJob && lastMetaJobSucceeded && (
                <Alert variant="success" className="mb-3">
                  <CheckCircle className="me-2" size={16} />
                  Last Meta catalog feed sync completed successfully at{' '}
                  {new Date(lastMetaCatalogFeedJob.stoppedAt || lastMetaCatalogFeedJob.createdAt).toLocaleString()}
                </Alert>
              )}

              {!isGmcSyncDisabledForEnvironment && hasCompletedMetaJob && lastMetaJobFailed && (
                <Alert variant="danger" className="mb-3">
                  <AlertCircle className="me-2" size={16} />
                  Last Meta catalog feed sync failed at{' '}
                  {new Date(lastMetaCatalogFeedJob.stoppedAt || lastMetaCatalogFeedJob.createdAt).toLocaleString()}
                </Alert>
              )}

              {!isGmcSyncDisabledForEnvironment &&
                !hasRunningMetaCatalogFeedJob &&
                !metaCatalogFeedError &&
                !hasCompletedMetaJob && (
                  <Alert variant="success" className="mb-3">
                    <CheckCircle className="me-2" size={16} />
                    Ready to sync catalog feed with Meta
                  </Alert>
                )}

              <Button
                variant="primary"
                onClick={handleGenerateMetaCatalogFeed}
                disabled={isMetaFeedDisabled}
                className="d-flex align-items-center"
              >
                {isGmcSyncDisabledForEnvironment ? (
                  <>
                    <Share2 className="me-2" size={16} />
                    Sync Catalog Feed with Meta
                  </>
                ) : isGeneratingMetaCatalogFeed ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Syncing...
                  </>
                ) : hasRunningMetaCatalogFeedJob ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Job in progress...
                  </>
                ) : (
                  <>
                    <Share2 className="me-2" size={16} />
                    Sync Catalog Feed with Meta
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SiteCacheManagementPage;
